/**
 * Routes SEO services localisées : FR sans préfixe, autres locales sous /{code}/services/…
 * Slugs métiers/villes conservés en français (canonical par locale).
 * Feature flags : REACT_APP_LOCALE_ROUTES_{ES,EN,DE,IT}=true en prod (actifs en dev).
 *
 * Néerlandais :
 * - nl-BE → hub marché `/be/nl` (pas de cluster `/nl/services`)
 * - nl-NL → préfixe `/nl` réservé Pays-Bas (actif en dev ; prod via REACT_APP_LOCALE_ROUTES_NL_NL)
 */

import {
  BOOKING_LANGUAGES,
  normalizeBookingLanguage,
  type BookingLanguageCode,
} from './i18n';
import { DUTCH_LOCALE_META, isDutchRegionalCode } from './dutchLocale';
import { SITE_URL } from './site';
import {
  getLocaleFromPath as getPathLocaleFromPath,
  isLocaleRoutesNlNlEnabled,
  stripPathLocalePrefix,
} from './localePathPrefix';
import { isBookingMetierSlug } from './metierPathSlugs';

export { isLocaleRoutesNlNlEnabled };

export const LOCALE_ROUTE_PREFIX: Record<BookingLanguageCode, string> = {
  fr: '',
  en: '/en',
  es: '/es',
  de: '/de',
  it: '/it',
  'nl-BE': '', // contenu Flandre via /be/nl, pas /nl/services
  'nl-NL': '/nl',
};

export const HREFLANG_CODE: Record<BookingLanguageCode, string> = {
  fr: 'fr',
  en: 'en',
  es: 'es',
  de: 'de',
  it: 'it',
  'nl-BE': DUTCH_LOCALE_META['nl-BE'].hreflang,
  'nl-NL': DUTCH_LOCALE_META['nl-NL'].hreflang,
};

export const OG_LOCALE: Record<BookingLanguageCode, string> = {
  fr: 'fr_FR',
  en: 'en_GB',
  es: 'es_ES',
  de: 'de_DE',
  it: 'it_IT',
  'nl-BE': DUTCH_LOCALE_META['nl-BE'].ogLocale,
  'nl-NL': DUTCH_LOCALE_META['nl-NL'].ogLocale,
};

function isLocaleRouteFlagEnabled(localeCode: string): boolean {
  const reactKey = `REACT_APP_LOCALE_ROUTES_${localeCode}`;
  const viteKey = `VITE_LOCALE_ROUTES_${localeCode}`;
  return (
    process.env[reactKey] === 'true' ||
    process.env[viteKey] === 'true' ||
    process.env.NODE_ENV === 'development'
  );
}

export function isLocaleRoutesEsEnabled(): boolean {
  return isLocaleRouteFlagEnabled('ES');
}

export function isLocaleRoutesEnEnabled(): boolean {
  return isLocaleRouteFlagEnabled('EN');
}

export function isLocaleRoutesDeEnabled(): boolean {
  return isLocaleRouteFlagEnabled('DE');
}

export function isLocaleRoutesItEnabled(): boolean {
  return isLocaleRouteFlagEnabled('IT');
}

export function isLocaleRouteEnabled(locale: BookingLanguageCode): boolean {
  if (locale === 'fr') return true;
  if (locale === 'es') return isLocaleRoutesEsEnabled();
  if (locale === 'en') return isLocaleRoutesEnEnabled();
  if (locale === 'de') return isLocaleRoutesDeEnabled();
  if (locale === 'it') return isLocaleRoutesItEnabled();
  // Flandre : pas de cluster SEO /nl/services (hub /be/nl)
  if (locale === 'nl-BE') return false;
  // Pays-Bas : opt-in explicite
  if (locale === 'nl-NL') return isLocaleRoutesNlNlEnabled();
  return false;
}

export function enabledLocales(): BookingLanguageCode[] {
  return BOOKING_LANGUAGES.filter(isLocaleRouteEnabled);
}

export function getLocaleFromPath(pathname: string): BookingLanguageCode | null {
  return getPathLocaleFromPath(pathname);
}

/** Langue UI implicite : préfixe URL (/en, /es…) ou français par défaut (sans préfixe). */
export function bookingLanguageFromPath(pathname: string): BookingLanguageCode {
  return getLocaleFromPath(pathname) ?? 'fr';
}

export function stripLocalePrefix(pathname: string): string {
  return stripPathLocalePrefix(pathname);
}

const SERVICES_PATH_RE = /^\/services\/([^/]+)(?:\/([^/]+))?\/?$/;
const METIER_SHORT_PATH_RE = /^\/([^/]+)(?:\/([^/]+))?\/?$/;

function parseMetierSegmentsFromStrippedPath(stripped: string): {
  metierSlug?: string;
  citySlug?: string;
} {
  const servicesMatch = stripped.match(SERVICES_PATH_RE);
  if (servicesMatch) {
    return { metierSlug: servicesMatch[1], citySlug: servicesMatch[2] };
  }
  const shortMatch = stripped.match(METIER_SHORT_PATH_RE);
  if (!shortMatch || stripped === '/') return {};
  const metierSlug = shortMatch[1];
  if (!isBookingMetierSlug(metierSlug)) return {};
  return { metierSlug, citySlug: shortMatch[2] };
}

export function isServicesPath(pathname: string): boolean {
  const { metierSlug } = parseMetierSegmentsFromStrippedPath(stripLocalePrefix(pathname));
  return Boolean(metierSlug);
}

export function parseServicesPath(pathname: string): {
  locale: BookingLanguageCode;
  metierSlug?: string;
  citySlug?: string;
} {
  const locale = getLocaleFromPath(pathname) ?? 'fr';
  const segments = parseMetierSegmentsFromStrippedPath(stripLocalePrefix(pathname));
  return {
    locale,
    metierSlug: segments.metierSlug,
    citySlug: segments.citySlug,
  };
}

export function servicesMetierPath(
  metierSlug: string,
  locale: BookingLanguageCode,
  citySlug?: string
): string {
  const prefix = LOCALE_ROUTE_PREFIX[locale];
  const base = `${prefix}/services/${metierSlug}`;
  return citySlug ? `${base}/${citySlug}` : base;
}

export function servicesMetierUrl(
  metierSlug: string,
  locale: BookingLanguageCode,
  citySlug?: string
): string {
  return `${SITE_URL}${servicesMetierPath(metierSlug, locale, citySlug)}`;
}

export type HreflangAlternate = { hrefLang: string; href: string };

function localeHubLocales(): BookingLanguageCode[] {
  const locales: BookingLanguageCode[] = ['fr', 'en', 'es', 'de', 'it'];
  if (isLocaleRoutesNlNlEnabled()) locales.push('nl-NL');
  return locales;
}

/** Locales hub/métier toujours routées côté client (SPA), même si flags prod absents (#1120 QA r3). */
function clientRouterHubLocaleCandidates(): BookingLanguageCode[] {
  const locales: BookingLanguageCode[] = ['en', 'es', 'de', 'it'];
  if (isLocaleRoutesNlNlEnabled()) locales.push('nl-NL');
  return locales;
}

export function clientRouterHubLocales(): BookingLanguageCode[] {
  const candidates = clientRouterHubLocaleCandidates();
  const enabled = new Set(enabledLocales());
  for (const locale of candidates) {
    enabled.add(locale);
  }
  return candidates.filter((l) => enabled.has(l));
}

/** Navigation globe / hub accueil : indépendant des flags prod (#1120 QA r4). */
export function isClientHubNavigationLocale(lang: BookingLanguageCode): boolean {
  if (lang === 'fr') return true;
  return clientRouterHubLocales().includes(lang);
}

export function servicesHubPath(locale: BookingLanguageCode): string {
  const prefix = LOCALE_ROUTE_PREFIX[locale];
  return prefix ? `${prefix}/services` : '/services';
}

export function servicesHubUrl(locale: BookingLanguageCode): string {
  return `${SITE_URL}${servicesHubPath(locale)}`;
}

export function getServicesHubHreflangAlternates(): HreflangAlternate[] {
  const alternates = localeHubLocales().map((locale) => ({
    hrefLang: HREFLANG_CODE[locale],
    href: servicesHubUrl(locale),
  }));
  alternates.push({ hrefLang: 'x-default', href: servicesHubUrl('fr') });
  return alternates;
}

export function localeHubPath(locale: BookingLanguageCode): string {
  const prefix = LOCALE_ROUTE_PREFIX[locale];
  return prefix ? `${prefix}/` : '/';
}

export function localeHubUrl(locale: BookingLanguageCode): string {
  const path = localeHubPath(locale);
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

/** Hreflang réciproques pour `/` et hubs `/en/`, `/es/`, `/de/`, `/it/` (#1120). */
export function getLocaleHubHreflangAlternates(): HreflangAlternate[] {
  const alternates = localeHubLocales().map((locale) => ({
    hrefLang: HREFLANG_CODE[locale],
    href: localeHubUrl(locale),
  }));
  alternates.push({ hrefLang: 'x-default', href: `${SITE_URL}/` });
  return alternates;
}

export function getServicesHreflangAlternates(
  metierSlug: string,
  citySlug?: string
): HreflangAlternate[] {
  const alternates = localeHubLocales().map((locale) => ({
    hrefLang: HREFLANG_CODE[locale],
    href: servicesMetierUrl(metierSlug, locale, citySlug),
  }));
  alternates.push({
    hrefLang: 'x-default',
    href: servicesMetierUrl(metierSlug, 'fr', citySlug),
  });
  return alternates;
}

export function localeFromLanguage(lang: string | null | undefined): BookingLanguageCode {
  return normalizeBookingLanguage(lang);
}

export function localizeServicesPath(pathname: string, targetLocale: BookingLanguageCode): string | null {
  const { metierSlug, citySlug } = parseServicesPath(pathname);
  if (!metierSlug) return null;
  return servicesMetierPath(metierSlug, targetLocale, citySlug);
}

/** Helper doc / tests : région SEO pour une locale UI. */
export function dutchSeoPolicy(locale: BookingLanguageCode): {
  region: 'BE' | 'NL' | null;
  servicesPrefixEnabled: boolean;
  marketHub: string | null;
} {
  if (!isDutchRegionalCode(locale)) {
    return { region: null, servicesPrefixEnabled: false, marketHub: null };
  }
  const meta = DUTCH_LOCALE_META[locale];
  return {
    region: meta.region,
    servicesPrefixEnabled: isLocaleRouteEnabled(locale),
    marketHub: meta.marketContentPath,
  };
}
